/**
 * Format playback speed for display
 * Removes trailing zeros and adds 'x' suffix
 * @param speed - Playback speed (e.g., 0.75, 1.0, 1.25)
 * @returns Formatted speed string (e.g., "0.75x", "1x", "1.25x")
 */
export const formatSpeed = (speed: number): string => {
  return speed.toFixed(2).replace(/\.?0+$/, '') + 'x';
};

/**
 * Calculate estimated module duration based on content
 */
interface ModuleContent {
  textContent?: string;
  audioDuration?: number; // seconds
  practiceExercises?: number;
  quizQuestions?: number;
  videoLinks?: number;
}

export const calculateModuleDuration = (content: ModuleContent): number => {
  let totalMinutes = 0;
  
  // Text reading (200 words/min average reading speed)
  if (content.textContent) {
    const wordCount = content.textContent.split(/\s+/).filter(word => word.length > 0).length;
    totalMinutes += wordCount / 200;
  }
  
  // Audio (actual duration if available)
  if (content.audioDuration) {
    totalMinutes += content.audioDuration / 60;
  }
  
  // Practice exercises (10 min each on average)
  if (content.practiceExercises) {
    totalMinutes += content.practiceExercises * 10;
  }
  
  // Quiz questions (2 min each for reading and answering)
  if (content.quizQuestions) {
    totalMinutes += content.quizQuestions * 2;
  }
  
  // Video content (assume 10 min each on average)
  if (content.videoLinks) {
    totalMinutes += content.videoLinks * 10;
  }
  
  return Math.ceil(totalMinutes);
};

/**
 * Format duration in minutes to human-readable string
 */
export const formatDuration = (minutes: number): string => {
  if (minutes < 60) {
    return `${minutes} min`;
  }
  
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  
  if (remainingMinutes === 0) {
    return `${hours} hr`;
  }
  
  return `${hours} hr ${remainingMinutes} min`;
};
